<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import type { IMenuItem } from '@/components/sidebar-menu/types'
  import type { AssetStoryblok } from '@/storyblok/component-types-sb'
  import type { ISbLink } from '@/storyblok/types'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'

  const props = withDefaults(
    defineProps<{
      menuItems: IMenuItem[]
      logoImage?: AssetStoryblok
      logoLink?: ISbLink
      ctaLink?: ISbLink
    }>(),
    {
      logoImage: undefined,
      logoLink: undefined,
      ctaLink: undefined
    }
  )

  const logoImageProps = computed(() => toAssetImageProps(props.logoImage))

  const visibleLogoLink = computed(() => {
    if (!props.logoImage?.filename || !props.logoLink?.href) return undefined

    return props.logoLink
  })

  const menuListClassVariants = cva(
    'm-0 flex min-w-0 flex-1 list-none flex-wrap items-stretch justify-start gap-x-1 gap-y-2 p-0',
    {
      variants: {
        hasLogo: {
          true: 'ml-14',
          false: ''
        }
      },
      defaultVariants: {
        hasLogo: false
      }
    }
  )

  const menuListClasses = computed(() =>
    menuListClassVariants({
      hasLogo: !!visibleLogoLink.value
    })
  )
</script>

<template>
  <div class="md:mx-10">
    <nav class="relative mx-auto mt-12 max-w-460 rounded-full bg-bt-green px-10 py-6 text-bt-blue-dark">
      <div class="relative flex w-full items-center">
        <div
          v-if="visibleLogoLink"
          class="flex shrink-0 items-center justify-start"
        >
          <NuxtLink
            :to="visibleLogoLink.href"
            :target="visibleLogoLink.target"
            class="flex shrink-0 items-center"
            external
          >
            <NuxtImg
              provider="storyblok"
              :src="logoImageProps.src"
              :alt="logoImageProps.alt"
              :title="logoImageProps.title"
              class="h-10 w-auto max-w-none shrink-0"
              loading="lazy"
            />
          </NuxtLink>
        </div>

        <ul :class="menuListClasses">
          <li
            v-for="item in menuItems"
            :key="item.href"
            class="m-0 flex"
          >
            <SidebarMenuItemLink
              :item="item"
              link-class="text-body-1 flex items-center whitespace-nowrap rounded-full px-4 py-2 no-underline transition-colors duration-300 hover:bg-bt-green-hover-muted"
              active-class="bg-bt-blue-dark font-medium text-white hover:bg-bt-blue-dark"
            />
          </li>
        </ul>

        <div
          v-if="ctaLink?.href && ctaLink.text"
          class="ml-10 flex shrink-0 items-center"
        >
          <UiLinkButton
            :title="ctaLink.text"
            :url="ctaLink.href"
            :target="ctaLink.target"
            variant="outline"
            size="sm"
            class="inline-flex min-w-24 justify-center border-bt-blue-ui font-medium"
          />
        </div>
      </div>
    </nav>
  </div>
</template>
